import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HomeRailProps {
  /** Short, factual heading — no "trending", nothing we can't substantiate. */
  title: string;
  /** One line saying where the cards come from. Optional. */
  subtitle?: string;
  /**
   * Where the equivalent full search lives — and it must genuinely *be*
   * equivalent: a rail headed "Open houses this weekend" whose link lands on
   * unfiltered results has promised something the destination doesn't keep, so
   * every curated rail derives this from the same filters it fetched with
   * (`lib/railLinks`).
   *
   * Omitted only when no such search exists. "Homes you looked at" is the
   * visitor's own history — there is no query that reproduces it, and inventing a
   * "See all" that lands somewhere else would be the exact mismatch above.
   */
  href?: string;
  seeAllLabel?: string;
  /** How many cards are in the rail, for the accessible name. */
  count: number;
  children: React.ReactNode;
  className?: string;
}

/**
 * One horizontally scrolling row of the curated home feed.
 *
 * Mobile 390 is the primary target, so the track is a scroll-snapping row with
 * cards at 78vw — wide enough to read, narrow enough that the next card peeks in
 * and advertises that the row scrolls. Desktop keeps the same row rather than
 * switching to a grid: a rail that reflows into a grid loses the "there is more
 * this way" affordance the feed depends on.
 *
 * Notes on the details that are easy to get wrong:
 * - `justify-start` unconditionally. A `justify-center` scroller whose content
 *   overflows pushes its leading cards off the left and makes them unreachable
 *   (W12 R118, W13 R122).
 * - The track is a `ul`/`li` list inside a labelled `section`, so a screen reader
 *   announces "Just listed, 12 homes, list, 12 items" instead of a wall of links.
 * - The track is focusable (`tabIndex=0`) so a keyboard user can arrow through it
 *   horizontally; without it an `overflow-x-auto` div is unreachable by keyboard
 *   except by tabbing card to card.
 * - `scroll-py`/`px-4` padding lives on the track, not a wrapper, so the first
 *   and last cards can still reach the viewport edges.
 */
export default function HomeRail({
  title,
  subtitle,
  href,
  seeAllLabel = 'See all',
  count,
  children,
  className,
}: HomeRailProps) {
  const headingId = `rail-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <section aria-labelledby={headingId} className={cn('py-6', className)}>
      <div className="mx-auto flex max-w-7xl items-end justify-between gap-4 px-4">
        <div className="min-w-0">
          <h2
            id={headingId}
            className="font-display text-2xl tracking-tight text-ink sm:text-3xl"
          >
            {title}
          </h2>
          {subtitle && <p className="mt-1 text-sm text-ink-muted">{subtitle}</p>}
        </div>
        {href && (
          <Link
            href={href}
            className="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium text-emerald-700 underline-offset-4 transition-colors hover:text-emerald-600 hover:underline"
          >
            {seeAllLabel}
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      <ul
        tabIndex={0}
        aria-label={`${title} — ${count} ${count === 1 ? 'home' : 'homes'}`}
        className="custom-scrollbar mt-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
      >
        {children}
      </ul>
    </section>
  );
}

/** A card slot in a rail. Sized here so every rail scrolls identically. */
export function HomeRailItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="w-[78vw] max-w-[320px] flex-shrink-0 snap-start sm:w-[300px]">{children}</li>
  );
}

/**
 * Card photo width for a rail card: a fixed **300 px** at ≥640 and 78vw below,
 * matching `HomeRailItem`. `ListingCard`'s default `sizes` describes a 3-column
 * grid and would pull a much wider source than a rail card ever paints.
 *
 * Lives here rather than in `FeedRails` so the client-side personalized rails can
 * share it: importing it from an async server component would drag `lib/api`
 * (and React's server-only `cache`) into the client bundle.
 */
export const RAIL_PHOTO_SIZES = '(max-width: 639px) 78vw, 300px';
